import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { BaseResponse } from 'app/core/models/common.response';
import {
    ChatSignalRService,
    IncomingMessage,
    PresenceEvent,
} from 'app/modules/admin/apps/chat/chat-signalr.service';
import { Chat, Contact, Message, Profile } from 'app/modules/admin/apps/chat/chat.types';
import {
    BehaviorSubject,
    Observable,
    map,
    of,
    switchMap,
    tap,
    throwError,
} from 'rxjs';

// ---- Backend DTOs (camelCase from the API) ------------------------------------------------

interface ConversationListItemDto {
    id: string;
    isGroup: boolean;
    title: string;
    avatar?: string;
    lastMessage?: string;
    lastMessageAt?: string;
    unreadCount: number;
    muted: boolean;
    otherUserId?: string;
    isOnline: boolean;
    lastSeenAt?: string;
}

interface ParticipantDto {
    userId: string;
    name: string;
    email?: string;
    isAdmin: boolean;
    isOnline: boolean;
    lastSeenAt?: string;
}

interface MessageDto {
    id: string;
    conversationId: string;
    senderId: string;
    senderName: string;
    content: string;
    messageType: string;
    attachmentUrl?: string;
    sentAt: string;
}

interface ConversationDto {
    id: string;
    isGroup: boolean;
    title: string;
    avatar?: string;
    unreadCount: number;
    muted: boolean;
    participants: ParticipantDto[];
    messages: MessageDto[];
}

interface ContactDto {
    id: string;
    name: string;
    email?: string;
    avatar?: string;
    isOnline: boolean;
    lastSeenAt?: string;
}

interface ProfileDto {
    id: string;
    name: string;
    email?: string;
    avatar?: string;
}

@Injectable({ providedIn: 'root' })
export class ChatService {
    private _chat: BehaviorSubject<Chat> = new BehaviorSubject(null);
    private _chats: BehaviorSubject<Chat[]> = new BehaviorSubject(null);
    private _contact: BehaviorSubject<Contact> = new BehaviorSubject(null);
    private _contacts: BehaviorSubject<Contact[]> = new BehaviorSubject(null);
    private _profile: BehaviorSubject<Profile> = new BehaviorSubject(null);

    private readonly _url = `${environment.baseUrl}/Chat`;
    private _myUserId: string | null = null;

    constructor(
        private _httpClient: HttpClient,
        private _signalr: ChatSignalRService
    ) {
        // React to live hub events.
        this._signalr.message$.subscribe((m) => this._onIncomingMessage(m));
        this._signalr.presence$.subscribe((p) => this._onPresence(p));
        this._signalr.onlineUsers$.subscribe((ids) => this._onOnlineUsers(ids));
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    get chat$(): Observable<Chat> {
        return this._chat.asObservable();
    }

    get chats$(): Observable<Chat[]> {
        return this._chats.asObservable();
    }

    get contact$(): Observable<Contact> {
        return this._contact.asObservable();
    }

    get contacts$(): Observable<Contact[]> {
        return this._contacts.asObservable();
    }

    get profile$(): Observable<Profile> {
        return this._profile.asObservable();
    }

    /** Total unread messages across all conversations (for the header chat-icon badge). */
    get totalUnread$(): Observable<number> {
        return this._chats
            .asObservable()
            .pipe(
                map((chats) =>
                    (chats || []).reduce((sum, c) => sum + (c.unreadCount || 0), 0)
                )
            );
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Connection
    // -----------------------------------------------------------------------------------------------------

    /** Start the realtime connection. Safe to call repeatedly. */
    connect(): void {
        void this._signalr.start();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    getChats(): Observable<Chat[]> {
        return this._httpClient
            .get<BaseResponse<ConversationListItemDto[]>>(`${this._url}/conversations`)
            .pipe(
                map((r) => (r.result || []).map((x) => this._mapListItem(x))),
                tap((chats) => this._chats.next(chats))
            );
    }

    getContacts(): Observable<Contact[]> {
        return this._httpClient
            .get<BaseResponse<ContactDto[]>>(`${this._url}/contacts`)
            .pipe(
                map((r) => (r.result || []).map((c) => this._mapContact(c))),
                tap((contacts) => this._contacts.next(contacts))
            );
    }

    getContact(id: string): Observable<Contact> {
        const found = (this._contacts.value || []).find((c) => c.id === id) || null;
        this._contact.next(found);
        return of(found);
    }

    getProfile(): Observable<Profile> {
        return this._httpClient
            .get<BaseResponse<ProfileDto>>(`${this._url}/profile`)
            .pipe(
                map((r) => r.result),
                tap((p) => {
                    this._myUserId = p?.id ?? null;
                    this._profile.next({
                        id: p?.id,
                        name: p?.name,
                        email: p?.email,
                        avatar: p?.avatar,
                        about: '',
                    });
                })
            );
    }

    getChatById(id: string): Observable<Chat> {
        return this._httpClient
            .get<BaseResponse<ConversationDto>>(`${this._url}/conversations/${id}`)
            .pipe(
                map((r) => this._mapConversation(r.result)),
                tap((chat) => {
                    this._chat.next(chat);
                    if (chat) {
                        this._resetUnreadInList(id);
                        void this._signalr.markRead(id);
                    }
                }),
                switchMap((chat) =>
                    chat
                        ? of(chat)
                        : throwError(() => 'Could not find chat with id of ' + id + '!')
                )
            );
    }

    /** Persist muted flag (and any future participant settings). */
    updateChat(id: string, chat: Chat): Observable<Chat> {
        return this._httpClient
            .patch<BaseResponse<boolean>>(`${this._url}/conversations/${id}`, {
                muted: chat.muted,
            })
            .pipe(
                map(() => {
                    const chats = this._chats.value || [];
                    const idx = chats.findIndex((c) => c.id === id);
                    if (idx > -1) {
                        chats[idx] = { ...chats[idx], muted: chat.muted };
                        this._chats.next([...chats]);
                    }
                    if (this._chat.value?.id === id) {
                        this._chat.next({ ...this._chat.value, muted: chat.muted });
                    }
                    return chat;
                })
            );
    }

    /** Get-or-create a 1-to-1 conversation with another user; resolves to the chat. */
    createDirectConversation(targetUserId: string): Observable<Chat> {
        return this._httpClient
            .post<BaseResponse<ConversationDto>>(`${this._url}/conversations/direct`, {
                targetUserId,
            })
            .pipe(
                map((r) => this._mapConversation(r.result)),
                tap((chat) => this._upsertChatInList(chat))
            );
    }

    /** Create a group conversation; resolves to the chat. */
    createGroupConversation(title: string, participantUserIds: string[]): Observable<Chat> {
        return this._httpClient
            .post<BaseResponse<ConversationDto>>(`${this._url}/conversations/group`, {
                title,
                participantUserIds,
            })
            .pipe(
                map((r) => this._mapConversation(r.result)),
                tap((chat) => this._upsertChatInList(chat))
            );
    }

    /**
     * Send a message. Prefer the realtime hub (echo arrives via ReceiveMessage); if the hub
     * is not connected, fall back to REST so the message still persists and shows locally.
     */
    sendMessage(conversationId: string, content: string): void {
        const text = (content || '').trim();
        if (!text) {
            return;
        }

        if (this._signalr.connected) {
            void this._signalr.sendMessage(conversationId, text);
            return;
        }

        this._httpClient
            .post<BaseResponse<MessageDto>>(`${this._url}/messages`, {
                conversationId,
                content: text,
            })
            .subscribe((r) => {
                if (r?.result) {
                    this._onIncomingMessage(r.result as IncomingMessage);
                }
            });
    }

    notifyTyping(conversationId: string): void {
        void this._signalr.typing(conversationId);
    }

    resetChat(): void {
        this._chat.next(null);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Realtime handlers
    // -----------------------------------------------------------------------------------------------------

    private _onIncomingMessage(m: IncomingMessage): void {
        const message: Message = this._mapMessage(m);

        const open = this._chat.value;
        const isOpen = open?.id === m.conversationId;

        // Append to the open conversation.
        if (open && open.id === m.conversationId) {
            const exists = (open.messages || []).some((x) => x.id === message.id);
            if (!exists) {
                const messages = [...(open.messages || []), message];
                this._chat.next({ ...open, messages });
            }
            void this._signalr.markRead(m.conversationId);
        }

        // Update the conversation list (last message, unread, ordering).
        const chats = this._chats.value || [];
        const idx = chats.findIndex((c) => c.id === m.conversationId);
        if (idx > -1) {
            const updated: Chat = {
                ...chats[idx],
                lastMessage: m.content,
                lastMessageAt: this._asUtcIso(m.sentAt),
                unreadCount:
                    isOpen || message.isMine
                        ? 0
                        : (chats[idx].unreadCount || 0) + 1,
            };
            const rest = chats.filter((_, i) => i !== idx);
            this._chats.next([updated, ...rest]);
        } else {
            // Conversation not in the list yet (e.g. someone messaged us for the first time).
            this.getChats().subscribe();
        }
    }

    private _onPresence(p: PresenceEvent): void {
        const lastSeenAt = this._asUtcIso(p.lastSeenAt);

        // Contacts
        const contacts = this._contacts.value;
        if (contacts) {
            this._contacts.next(
                contacts.map((c) =>
                    c.id === p.userId
                        ? { ...c, isOnline: p.isOnline, lastSeenAt: lastSeenAt }
                        : c
                )
            );
        }

        // Conversation list (direct chats)
        const chats = this._chats.value;
        if (chats) {
            this._chats.next(
                chats.map((c) =>
                    c.otherUserId === p.userId
                        ? {
                              ...c,
                              contact: {
                                  ...c.contact,
                                  isOnline: p.isOnline,
                                  lastSeenAt: lastSeenAt,
                              },
                          }
                        : c
                )
            );
        }

        // Open conversation
        const open = this._chat.value;
        if (open) {
            let changed = false;
            const participants = (open.participants || []).map((pp) => {
                if (pp.userId === p.userId) {
                    changed = true;
                    return { ...pp, isOnline: p.isOnline, lastSeenAt: lastSeenAt };
                }
                return pp;
            });
            const contact =
                open.otherUserId === p.userId
                    ? { ...open.contact, isOnline: p.isOnline, lastSeenAt: lastSeenAt }
                    : open.contact;
            if (changed || open.otherUserId === p.userId) {
                this._chat.next({ ...open, participants, contact });
            }
        }
    }

    private _onOnlineUsers(ids: string[]): void {
        const set = new Set(ids);

        const contacts = this._contacts.value;
        if (contacts) {
            this._contacts.next(contacts.map((c) => ({ ...c, isOnline: set.has(c.id) })));
        }

        const chats = this._chats.value;
        if (chats) {
            this._chats.next(
                chats.map((c) =>
                    c.otherUserId
                        ? {
                              ...c,
                              contact: { ...c.contact, isOnline: set.has(c.otherUserId) },
                          }
                        : c
                )
            );
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Helpers / mappers
    // -----------------------------------------------------------------------------------------------------

    private _resetUnreadInList(id: string): void {
        const chats = this._chats.value || [];
        const idx = chats.findIndex((c) => c.id === id);
        if (idx > -1 && chats[idx].unreadCount) {
            chats[idx] = { ...chats[idx], unreadCount: 0 };
            this._chats.next([...chats]);
        }
    }

    private _upsertChatInList(chat: Chat): void {
        if (!chat) {
            return;
        }
        const chats = this._chats.value || [];
        if (!chats.some((c) => c.id === chat.id)) {
            this._chats.next([chat, ...chats]);
        }
    }

    private _mapListItem(x: ConversationListItemDto): Chat {
        return {
            id: x.id,
            isGroup: x.isGroup,
            unreadCount: x.unreadCount,
            muted: x.muted,
            lastMessage: x.lastMessage,
            lastMessageAt: this._asUtcIso(x.lastMessageAt),
            otherUserId: x.otherUserId,
            contactId: x.otherUserId ?? x.id,
            contact: {
                id: x.otherUserId ?? x.id,
                name: x.title || 'Chat',
                avatar: x.avatar,
                isOnline: x.isOnline,
                lastSeenAt: this._asUtcIso(x.lastSeenAt),
                about: '',
            },
        };
    }

    private _mapConversation(c: ConversationDto): Chat {
        if (!c) {
            return null;
        }
        const other = (c.participants || []).find((p) => p.userId !== this._myUserId);
        const messages = (c.messages || []).map((m) => this._mapMessage(m));

        const contact: Contact = c.isGroup
            ? { id: c.id, name: c.title || 'Group', avatar: c.avatar, about: '' }
            : {
                  id: other?.userId ?? c.id,
                  name: other?.name || c.title || 'Chat',
                  email: other?.email,
                  avatar: c.avatar,
                  isOnline: other?.isOnline ?? false,
                  lastSeenAt: this._asUtcIso(other?.lastSeenAt),
                  about: '',
              };

        return {
            id: c.id,
            isGroup: c.isGroup,
            unreadCount: c.unreadCount,
            muted: c.muted,
            otherUserId: c.isGroup ? undefined : other?.userId,
            contactId: contact.id,
            contact,
            participants: (c.participants || []).map((p) => ({ ...p })),
            messages,
            lastMessage: messages.length ? messages[messages.length - 1].value : '',
            lastMessageAt: messages.length
                ? messages[messages.length - 1].createdAt
                : undefined,
        };
    }

    private _mapMessage(m: MessageDto | IncomingMessage): Message {
        return {
            id: m.id,
            chatId: m.conversationId,
            contactId: m.senderId,
            isMine: !!this._myUserId && m.senderId === this._myUserId,
            value: m.content,
            createdAt: this._asUtcIso(m.sentAt),
        };
    }

    private _mapContact(c: ContactDto): Contact {
        return {
            id: c.id,
            name: c.name,
            email: c.email,
            avatar: c.avatar,
            isOnline: c.isOnline,
            lastSeenAt: this._asUtcIso(c.lastSeenAt),
            about: '',
        };
    }

    /**
     * Chat timestamps are UTC instants. If the API returns one without a timezone marker
     * (no trailing "Z" or +/-offset), append "Z" so Angular's date pipe converts to local
     * time instead of treating it as already-local.
     */
    private _asUtcIso(value?: string): string | undefined {
        if (!value) {
            return value;
        }
        const hasTz = /[zZ]$/.test(value) || /[+-]\d{2}:?\d{2}$/.test(value);
        return hasTz ? value : value + 'Z';
    }
}
